/** @module @liquidefy/code-editor/circuits/project-navigator @version 1.0.0 @license LQD Research LLC */

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  EditorResult,
  FileEntry,
  DirectoryEntry,
  SearchQuery,
  SearchMatch,
  SearchResult,
  FileId,
  DirectoryId,
  DocumentEncoding,
  ProjectType,
} from '../code-editor-types.js';

import { ok, err, generateId } from '../code-editor.js';

import { buildFileTree, addToTree, removeFromTree, moveInTree, getNode, walkTree, nodeCount, treeDepth, filterByExtension } from '../filesystem/tree.js';
import { searchInContent, searchInFiles, totalMatchCount } from '../filesystem/search.js';
import { detectProjectType, detectPackageManager } from '../project/detection.js';


// ═══════════════════════════════════════════════════════════════════════════════
// §1 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface ProjectNavigatorConfig {
  readonly rootPath?: string;
  readonly entries?: FileEntry[];
  readonly showHidden?: boolean;
  readonly sortBy?: 'name' | 'type' | 'size' | 'date';
}

export interface NavigatorSearchResult {
  readonly path: string;
  readonly name: string;
  readonly matches: number;
  readonly isDirectory: boolean;
}

export interface TreeStats {
  readonly fileCount: number;
  readonly dirCount: number;
  readonly totalSize: number;
}

export interface ProjectNavigatorHandle extends CircuitHandle {
  getTree(): DirectoryEntry;
  navigate(path: string): FileEntry | DirectoryEntry | undefined;
  addFile(path: string, entry: FileEntry): EditorResult<DirectoryEntry>;
  removeFile(path: string): EditorResult<DirectoryEntry>;
  moveFile(from: string, to: string): EditorResult<DirectoryEntry>;
  search(query: string): NavigatorSearchResult[];
  getProjectType(): string;
  getFileLanguage(path: string): string;
  getTreeStats(): TreeStats;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §2 INTERNAL STATE
// ═══════════════════════════════════════════════════════════════════════════════

interface InternalNavState {
  tree: DirectoryEntry;
  rootPath: string;
  showHidden: boolean;
  sortBy: 'name' | 'type' | 'size' | 'date';
  fileIndex: Map<string, FileEntry>;
  dirIndex: Map<string, DirectoryEntry>;
  projectType: string | null;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §3 PURE HELPERS — language detection, tree traversal, sorting
// ═══════════════════════════════════════════════════════════════════════════════

const EXTENSION_MAP: Record<string, string> = {
  '.ts': 'typescript', '.tsx': 'typescriptreact', '.js': 'javascript', '.jsx': 'javascriptreact',
  '.py': 'python', '.rb': 'ruby', '.rs': 'rust', '.go': 'go', '.java': 'java',
  '.c': 'c', '.cpp': 'cpp', '.h': 'c', '.hpp': 'cpp', '.cs': 'csharp',
  '.swift': 'swift', '.kt': 'kotlin', '.scala': 'scala', '.clj': 'clojure',
  '.html': 'html', '.css': 'css', '.scss': 'scss', '.less': 'less', '.sass': 'sass',
  '.json': 'json', '.yaml': 'yaml', '.yml': 'yaml', '.toml': 'toml', '.xml': 'xml',
  '.md': 'markdown', '.mdx': 'mdx', '.txt': 'plaintext', '.csv': 'csv',
  '.sql': 'sql', '.graphql': 'graphql', '.gql': 'graphql',
  '.sh': 'shellscript', '.bash': 'shellscript', '.zsh': 'shellscript', '.fish': 'shellscript',
  '.ps1': 'powershell', '.bat': 'bat', '.cmd': 'bat',
  '.dockerfile': 'dockerfile', '.dockerignore': 'dockerignore',
  '.svelte': 'svelte', '.vue': 'vue', '.astro': 'astro',
  '.r': 'r', '.R': 'r', '.lua': 'lua', '.zig': 'zig', '.nim': 'nim',
  '.ex': 'elixir', '.exs': 'elixir', '.erl': 'erlang', '.hrl': 'erlang',
  '.dart': 'dart', '.php': 'php', '.pl': 'perl', '.pm': 'perl',
  '.wasm': 'wasm', '.wat': 'wat',
};

const FILENAME_MAP: Record<string, string> = {
  'Dockerfile': 'dockerfile', 'Makefile': 'makefile', 'Justfile': 'makefile',
  'Rakefile': 'ruby', 'Gemfile': 'ruby', 'Cargo.toml': 'toml',
  'package.json': 'json', 'tsconfig.json': 'json', '.gitignore': 'ignore',
  '.eslintrc': 'json', '.prettierrc': 'json', 'docker-compose.yml': 'yaml',
};

function detectLanguage(path: string): string {
  const filename = path.split('/').pop() || '';

  if (FILENAME_MAP[filename]) return FILENAME_MAP[filename];

  const lastDot = filename.lastIndexOf('.');
  if (lastDot >= 0) {
    const ext = filename.slice(lastDot);
    if (EXTENSION_MAP[ext]) return EXTENSION_MAP[ext];
  }

  return 'plaintext';
}

function detectProjectTypeFromTree(tree: DirectoryEntry): string {
  const files: string[] = [];
  walkTree(tree, (node: FileEntry | DirectoryEntry) => {
    if (!('children' in node)) files.push(node.name);
  });
  return detectProjectType(files) ?? 'unknown';
}

function buildIndexes(tree: DirectoryEntry): { files: Map<string, FileEntry>; dirs: Map<string, DirectoryEntry> } {
  const files = new Map<string, FileEntry>();
  const dirs = new Map<string, DirectoryEntry>();

  dirs.set(tree.path, tree);

  walkTree(tree, (node: FileEntry | DirectoryEntry) => {
    if ('children' in node) {
      dirs.set(node.path, node);
    } else {
      files.set(node.path, node);
    }
  });

  return { files, dirs };
}

function collectAllFiles(tree: DirectoryEntry): FileEntry[] {
  const files: FileEntry[] = [];
  walkTree(tree, (node: FileEntry | DirectoryEntry) => {
    if (!('children' in node)) files.push(node);
  });
  return files;
}

function computeTreeStats(tree: DirectoryEntry): TreeStats {
  let fileCount = 0;
  let dirCount = 0;
  let totalSize = 0;

  walkTree(tree, (node: FileEntry | DirectoryEntry) => {
    if ('children' in node) {
      dirCount++;
    } else {
      fileCount++;
      totalSize += node.size;
    }
  });

  return { fileCount, dirCount, totalSize };
}

function sortChildren(children: (FileEntry | DirectoryEntry)[], sortBy: 'name' | 'type' | 'size' | 'date'): (FileEntry | DirectoryEntry)[] {
  return [...children].sort((a, b) => {
    // Directories first
    const aIsDir = 'children' in a;
    const bIsDir = 'children' in b;
    if (aIsDir && !bIsDir) return -1;
    if (!aIsDir && bIsDir) return 1;

    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'type': {
        const aExt = 'extension' in a ? (a as FileEntry).extension : '';
        const bExt = 'extension' in b ? (b as FileEntry).extension : '';
        return aExt.localeCompare(bExt) || a.name.localeCompare(b.name);
      }
      case 'size': {
        const aSize = 'size' in a ? (a as FileEntry).size : 0;
        const bSize = 'size' in b ? (b as FileEntry).size : 0;
        return bSize - aSize;
      }
      case 'date': {
        const aDate = 'modifiedAt' in a ? (a as FileEntry).modifiedAt : 0;
        const bDate = 'modifiedAt' in b ? (b as FileEntry).modifiedAt : 0;
        return bDate - aDate;
      }
      default:
        return a.name.localeCompare(b.name);
    }
  });
}

function filterHidden(tree: DirectoryEntry, showHidden: boolean): DirectoryEntry {
  if (showHidden) return tree;

  const filteredChildren = tree.children
    .filter(child => !child.name.startsWith('.'))
    .map(child => {
      if ('children' in child) {
        return filterHidden(child, showHidden);
      }
      return child;
    });

  return { ...tree, children: filteredChildren };
}

function searchInTree(tree: DirectoryEntry, query: string): NavigatorSearchResult[] {
  const results: NavigatorSearchResult[] = [];
  const q = query.toLowerCase();

  walkTree(tree, (node: FileEntry | DirectoryEntry) => {
    if ('children' in node) {
      if (node.name.toLowerCase().includes(q)) {
        results.push({
          path: node.path,
          name: node.name,
          matches: 1,
          isDirectory: true,
        });
      }
    } else {
      const nameMatch = node.name.toLowerCase().includes(q);
      const pathMatch = node.path.toLowerCase().includes(q);
      if (nameMatch || pathMatch) {
        results.push({
          path: node.path,
          name: node.name,
          matches: (nameMatch ? 2 : 0) + (pathMatch ? 1 : 0),
          isDirectory: false,
        });
      }
    }
  });

  return results.sort((a, b) => b.matches - a.matches);
}

function createEmptyTree(rootPath: string): DirectoryEntry {
  return {
    id: generateId('dir') as DirectoryId,
    path: rootPath,
    name: rootPath.split('/').pop() || 'root',
    children: [],
    isExpanded: true,
  };
}


// ═══════════════════════════════════════════════════════════════════════════════
// §4 FACTORY — createProjectNavigator
// ═══════════════════════════════════════════════════════════════════════════════

export function createProjectNavigator(config: ProjectNavigatorConfig): ProjectNavigatorHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // Build initial tree
  const rootPath = config.rootPath ?? '/';
  let tree: DirectoryEntry;

  if (config.entries && config.entries.length > 0) {
    const treeResult = buildFileTree(config.entries, rootPath);
    tree = treeResult.success ? treeResult.value : createEmptyTree(rootPath);
  } else {
    tree = createEmptyTree(rootPath);
  }

  const indexes = buildIndexes(tree);

  const state: InternalNavState = {
    tree,
    rootPath,
    showHidden: config.showHidden ?? false,
    sortBy: config.sortBy ?? 'name',
    fileIndex: indexes.files,
    dirIndex: indexes.dirs,
    projectType: null,
  };

  function rebuildIndexes(): void {
    const idx = buildIndexes(state.tree);
    state.fileIndex = idx.files;
    state.dirIndex = idx.dirs;
  }

  return Object.freeze({
    name: 'project-navigator',

    getTree(): DirectoryEntry {
      guardDestroyed();
      let result = filterHidden(state.tree, state.showHidden);
      result = {
        ...result,
        children: sortChildren(result.children, state.sortBy),
      };
      return result;
    },

    navigate(path: string): FileEntry | DirectoryEntry | undefined {
      guardDestroyed();
      try {
        const node = getNode(state.tree, path);
        circuitStats = recordSuccess(circuitStats);
        return node;
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return undefined;
      }
    },

    addFile(path: string, entry: FileEntry): EditorResult<DirectoryEntry> {
      guardDestroyed();
      try {
        const result = addToTree(state.tree, entry);
        if (!result.success) {
          circuitStats = recordError(circuitStats);
          return result;
        }
        state.tree = result.value;
        rebuildIndexes();
        circuitStats = recordSuccess(circuitStats);
        return ok(state.tree, 'FILE_ADDED', `Added ${entry.name} to tree`);
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('ADD_FAILED', `Failed to add file: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    removeFile(path: string): EditorResult<DirectoryEntry> {
      guardDestroyed();
      try {
        const result = removeFromTree(state.tree, path);
        if (!result.success) {
          circuitStats = recordError(circuitStats);
          return result;
        }
        state.tree = result.value;
        rebuildIndexes();
        circuitStats = recordSuccess(circuitStats);
        return ok(state.tree, 'FILE_REMOVED', `Removed ${path} from tree`);
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('REMOVE_FAILED', `Failed to remove file: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    moveFile(from: string, to: string): EditorResult<DirectoryEntry> {
      guardDestroyed();
      try {
        const result = moveInTree(state.tree, from, to);
        if (!result.success) {
          circuitStats = recordError(circuitStats);
          return result;
        }
        state.tree = result.value;
        rebuildIndexes();
        circuitStats = recordSuccess(circuitStats);
        return ok(state.tree, 'FILE_MOVED', `Moved ${from} to ${to}`);
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('MOVE_FAILED', `Failed to move file: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    search(query: string): NavigatorSearchResult[] {
      guardDestroyed();
      try {
        const results = searchInTree(state.tree, query);
        circuitStats = recordSuccess(circuitStats);
        return results;
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return [];
      }
    },

    getProjectType(): string {
      guardDestroyed();
      if (state.projectType) return state.projectType;

      try {
        const detected = detectProjectTypeFromTree(state.tree);
        state.projectType = detected;
        return detected;
      } catch {
        return 'unknown';
      }
    },

    getFileLanguage(path: string): string {
      guardDestroyed();
      return detectLanguage(path);
    },

    getTreeStats(): TreeStats {
      guardDestroyed();
      return computeTreeStats(state.tree);
    },

    stats(): CircuitStats {
      return { ...circuitStats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('project-navigator', 'destroyed', 'Circuit destroyed', createdAt);
      const treeStats = computeTreeStats(state.tree);
      const details = `${formatStatsDetails(circuitStats)}, Files: ${treeStats.fileCount}, Dirs: ${treeStats.dirCount}`;
      return buildHealthReport('project-navigator', healthFromStats(circuitStats), details, createdAt);
    },

    destroy(): void {
      destroyed = true;
      state.fileIndex.clear();
      state.dirIndex.clear();
    },
  });
}
